import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import { Collapse, Grid, IconButton } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'
import {
  DateUtils,
  type Nil,
  NumberUtils,
  PuiTooltip,
  TextWithTooltip,
  Utils,
} from '@pbt/pbt-ui-components'
import {
  Edit as EditIcon,
  Warning as WarningIcon,
} from '@pbt/pbt-ui-components/src/icons'
import { formatMoney } from '@pbt/pbt-ui-components/src/utils/numberUtils'

import type {
  Client,
  Payment as GraphQlPayment,
  RetailOrder,
  RetailPaymentDetail,
  Totals,
} from '~/api/graphql/generated/types'
import MenuDropdown from '~/components/common/inputs/MenuDropdown'
import { CollapsibleInformationList } from '~/components/elements/CollapsibleInformationList/CollapsibleInformationList'
import Typography from '~/components/elements/Typography/Typography'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import i18nPortal from '~/locales/i18n'
import {
  fetchClientBillingActivity,
  getBillingActivitySummary,
} from '~/store/duck/clientBillingActivityData'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import { useGetChewyOrderState } from '~/store/hooks/retailOrderItems'
import {
  getChewyFreeShippingEnabled,
  getFeatureToggle,
} from '~/store/reducers/constants'
import { getUser } from '~/store/reducers/users'
import type {
  BatchInvoice,
  InvoiceOrEstimate,
  InvoiceTotals,
  Payment,
} from '~/types'
import { getIsFreeShippingEligible } from '~/utils/cvcClient'
import {
  formatMoneyRange,
  formatPositiveMoney,
  getIsBatchInvoice,
} from '~/utils/invoiceUtils'
import useDialog from '~/utils/useDialog'

import { ChewyPaymentTile } from './ChewyPaymentTile'
import { InvoicePaymentTile } from './InvoicePaymentTile'
import {
  calculateDeposit,
  calculateDiscountAmount,
  calculateOverageAmount,
  calculateTotalAmount,
  getClientBalanceData,
  getCollapsibleTitle,
  getInvoiceStateFlags,
  getInvoiceTotalDueLabel,
  getShippingLabel,
  getSubtotalValue,
  getTaxValue,
  getTitleValue,
} from './invoiceTotalsHelpers'

export const useInvoiceTotalsSectionStyles = makeStyles(
  (theme) => ({
    totalContainer: {
      backgroundColor: theme.colors.tableLeftColumnBackground,
      borderLeft: theme.constants.tabBorder,
    },
    row: {
      '&:not(:last-of-type)': {
        borderBottom: theme.constants.totalBorder,
      },
    },
    leftColumn: {
      borderRight: theme.constants.totalBorder,
    },
    expander: {
      cursor: 'pointer',
    },
    warningIcon: {
      height: '15px',
      marginTop: theme.spacing(0.25),
      fill: theme.colors.uiNeedsAttention,
    },
    shippingWrapper: {
      display: 'flex',
    },
    collapse: {
      paddingLeft: theme.spacing(3),
    },
    menuDropdownButton: {
      fontWeight: 500,
      paddingLeft: 0,
      paddingRight: 0,
      textAlign: 'left',
    },
    iconButton: {
      padding: theme.spacing(1),
    },
  }),
  { name: 'InvoiceTotalsSection' },
)

const MINIMUM_AMOUNT_OF_PAYMENTS_TO_COLLAPSE = 3

const ServiceFeeOptions = [
  {
    name: i18nPortal.t('Invoices:WITH_SERVICE_FEE'),
    value: true,
  },
  {
    name: i18nPortal.t('Invoices:WITHOUT_SERVICE_FEE'),
    value: false,
  },
]

export interface InvoiceTotalsSectionProps {
  clientId: string | Nil
  includeServiceFee: boolean
  invoice: (InvoiceOrEstimate | BatchInvoice) & {
    grandTotal?: Totals
    retailOrder?: RetailOrder
  }
  isEstimate: boolean
  navigateToRefundLanding?: () => void
  setIncludeServiceFee: (arg: boolean) => void
  setInvoice: (invoice: InvoiceOrEstimate) => void
  totals: InvoiceTotals
}

const InvoiceTotalsSection = ({
  clientId,
  invoice,
  setInvoice,
  isEstimate,
  includeServiceFee,
  setIncludeServiceFee,
  navigateToRefundLanding,
  totals,
}: InvoiceTotalsSectionProps) => {
  const classes = useInvoiceTotalsSectionStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation(['Common', 'Invoices'])

  const client = useSelector(getUser(clientId))
  const clientBillingActivitySummary = useSelector(getBillingActivitySummary)
  const isDiscountReasonEnabled = useSelector(
    getFeatureToggle(FeatureToggle.DISCOUNT_REASON),
  )
  const isChewyFreeShippingEnabled = useSelector(getChewyFreeShippingEnabled)

  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()
  const [openRequiredDepositDialog] = useDialog(DialogNames.REQUIRED_DEPOSIT)

  const [isPaymentsCollapsed, setIsPaymentsCollapsed] = useState(false)

  const { payments: invoicePayments = [], retailOrder, grandTotal } = invoice

  const getChewyOrderState = useGetChewyOrderState()
  const { isMovedToChewyCart } = getChewyOrderState(retailOrder?.state?.id)

  const isBatchInvoice = getIsBatchInvoice(invoice)
  const chewyPayments = retailOrder?.paymentDetails || []

  const completedPayments = useMemo(() => {
    const isChewyCheckoutEnabledAndHasRetailOrder =
      isChewyCheckoutEnabled && Boolean(retailOrder) && !isMovedToChewyCart
    const hasChewyPayments =
      isChewyCheckoutEnabledAndHasRetailOrder && !R.isEmpty(chewyPayments)
    return hasChewyPayments
      ? [...invoicePayments, ...chewyPayments]
      : invoicePayments
  }, [
    isChewyCheckoutEnabled,
    retailOrder,
    isMovedToChewyCart,
    invoicePayments?.length,
    chewyPayments?.length,
  ])

  const flags = getInvoiceStateFlags({
    invoice,
    isEstimate,
    isChewyCheckoutEnabled,
    isMovedToChewyCart,
    retailOrder,
    client: client as unknown as Client,
    completedPaymentsLength: completedPayments.length,
    isBatchInvoice,
    minimumPaymentsToCollapse: MINIMUM_AMOUNT_OF_PAYMENTS_TO_COLLAPSE,
  })

  const deposit = calculateDeposit(includeServiceFee, totals)
  const totalAmount = calculateTotalAmount(
    isEstimate,
    includeServiceFee,
    invoice,
    totals,
  )

  const isChewyPayment = (
    payment: GraphQlPayment | RetailPaymentDetail | Payment,
  ): payment is RetailPaymentDetail =>
    '__typename' in payment && payment.__typename === 'RetailPaymentDetail'

  const overageAmount = calculateOverageAmount(
    completedPayments,
    isChewyPayment,
  )

  const { clientBalanceDate, clientBalance } = getClientBalanceData(
    invoice,
    clientBillingActivitySummary.balance,
  )

  const discountAmount = calculateDiscountAmount(
    isEstimate,
    flags.isChewyCheckoutEnabledAndHasRetailOrder,
    invoice,
    grandTotal,
    totals,
  )

  const { paymentMethods = [] } = (client as unknown as Client) || {}
  const isFreeShippingEligible = getIsFreeShippingEligible(paymentMethods)

  useEffect(() => {
    if (completedPayments) {
      setIsPaymentsCollapsed(flags.showTotalPaidLabel)
    }
  }, [completedPayments])

  useEffect(() => {
    if (clientId) {
      dispatch(fetchClientBillingActivity(clientId))
    }
  }, [clientId])

  return (
    <Grid
      container
      item
      className={classes.totalContainer}
      direction="column"
      width={isEstimate ? 500 : '100%'}
      wrap="nowrap"
    >
      <Grid item className={classes.row}>
        <CollapsibleInformationList
          ContainerProps={{ pr: 3, py: 1 }}
          border={{ position: '33.33%', color: '#E6E8E9' }}
          initiallyOpened={false}
          list={[
            ...(R.prop('showBalance', invoice) && !isEstimate
              ? [
                  {
                    id: 'previous_balance',
                    label: t('Common:PREVIOUS_BALANCE'),
                    value: NumberUtils.formatMoney(
                      R.prop('prevBalance', invoice),
                    ),
                  },
                ]
              : []),
            {
              id: 'subtotal',
              label: t('Common:SUBTOTAL'),
              value: formatMoneyRange(
                getSubtotalValue(
                  isEstimate,
                  flags.isChewyCheckoutEnabledAndHasRetailOrder,
                  invoice,
                  grandTotal,
                  totals,
                ),
              ),
            },
            {
              id: 'discount',
              label: (
                <TextWithTooltip
                  tooltipText={
                    isDiscountReasonEnabled && invoice.discountReasonName
                  }
                  variant="body2"
                >
                  {t('Common:DISCOUNT')}
                </TextWithTooltip>
              ),
              value: formatMoneyRange(discountAmount, true),
            },
            {
              id: 'tax',
              label: t('Common:TAX'),
              value: formatMoneyRange(
                getTaxValue(
                  isEstimate,
                  flags.isChewyCheckoutEnabledAndHasRetailOrder,
                  invoice,
                  grandTotal,
                  totals,
                ),
              ),
            },
            ...(flags.isChewyCheckoutEnabledAndHasRetailOrder
              ? [
                  {
                    id: 'shipping',
                    label: (
                      <div className={classes.shippingWrapper}>
                        <Typography.Paragraph>
                          {getShippingLabel(
                            isChewyFreeShippingEnabled,
                            grandTotal?.shipping,
                            isFreeShippingEligible,
                            t,
                          )}
                        </Typography.Paragraph>
                        {flags.showMissingShippingAddressWarning &&
                        isChewyCheckoutEnabled ? (
                          <PuiTooltip
                            tooltipPlacement="top-end"
                            tooltipText={t(
                              'Common:MISSING_SHIPPING_ADDRESS_TOOLTIP',
                              {
                                name: client?.firstName,
                              },
                            )}
                          >
                            <WarningIcon className={classes.warningIcon} />
                          </PuiTooltip>
                        ) : null}
                      </div>
                    ),
                    // Currently, the backend value is always 0. This rule may need to be reviewed in the future.
                    value: NumberUtils.formatMoney(grandTotal?.shipping),
                  },
                  ...(retailOrder && retailOrder.retailDeliveryFee > 0
                    ? [
                        {
                          id: 'retail_delivery_fee',
                          label: t('Common:RETAIL_DELIVERY_FEE'),
                          value: NumberUtils.formatMoney(
                            retailOrder.retailDeliveryFee,
                          ),
                        },
                      ]
                    : []),
                ]
              : []),
          ]}
          title={getCollapsibleTitle(
            flags.isBatchInvoice,
            isChewyCheckoutEnabled,
            isEstimate,
            invoice.invoices?.length,
            t,
          )}
          titleValue={formatMoneyRange(
            getTitleValue(
              isEstimate,
              flags.isChewyCheckoutEnabledAndHasRetailOrder,
              invoice,
              grandTotal,
              totals,
            ),
          )}
        />
      </Grid>
      {flags.showServiceFeeOptions && (
        <Grid container item alignItems="center" className={classes.row}>
          <Grid container item className={classes.leftColumn} pl={3} xs={8}>
            <MenuDropdown
              linkButton
              classes={{
                button: classes.menuDropdownButton,
              }}
              items={ServiceFeeOptions}
              title={
                ServiceFeeOptions.find(
                  (option) => option.value === includeServiceFee,
                )?.name
              }
              onSelected={setIncludeServiceFee}
            />
          </Grid>
          <Grid item pr={3} xs={4}>
            <Typography.H3 sx={{ textAlign: 'right' }}>
              {formatMoneyRange(totalAmount)}
            </Typography.H3>
          </Grid>
        </Grid>
      )}
      {flags.hasDeposit && (
        <Grid container item alignItems="center" className={classes.row}>
          <Grid
            container
            item
            alignItems="center"
            className={classes.leftColumn}
            pl={3}
            wrap="nowrap"
            xs={8}
          >
            <Typography.H3 sx={{ textAlign: 'right' }}>
              {t('Invoices:REQUIRED_DEPOSIT_AMOUNT', {
                amount: NumberUtils.toPercentFormat(invoice.requiredDeposit, 2),
              })}
            </Typography.H3>
            <IconButton
              className={classes.iconButton}
              size="large"
              onClick={() => {
                openRequiredDepositDialog({
                  estimate: invoice as InvoiceOrEstimate,
                  onChange: setInvoice,
                })
              }}
            >
              <EditIcon />
            </IconButton>
          </Grid>
          <Grid item pr={3} xs={4}>
            <Typography.H3 sx={{ textAlign: 'right' }}>
              {formatMoneyRange(deposit)}
            </Typography.H3>
          </Grid>
        </Grid>
      )}
      {!R.isEmpty(completedPayments) && (
        <Grid className={classes.row}>
          {flags.showTotalPaidLabel && (
            <Grid
              container
              item
              alignItems="center"
              className={classNames(classes.expander, {
                [classes.row]: !isPaymentsCollapsed,
              })}
              onClick={() => setIsPaymentsCollapsed(!isPaymentsCollapsed)}
            >
              <Grid
                container
                item
                alignItems="center"
                className={classes.leftColumn}
                py={1.5}
                wrap="nowrap"
                xs={8}
              >
                {isPaymentsCollapsed ? (
                  <KeyboardArrowRight />
                ) : (
                  <KeyboardArrowUp />
                )}
                <Typography.H3>
                  {t('Invoices:TOTAL_PAID_COMPLETED_PAYMENTS', {
                    completedPayments: completedPayments.length,
                  })}
                </Typography.H3>
              </Grid>
              <Grid item pr={3} xs={4}>
                <Typography.H3 sx={{ textAlign: 'right' }}>
                  {formatMoneyRange(invoice.paidAmount, true)}
                </Typography.H3>
              </Grid>
            </Grid>
          )}
          <Collapse in={!isPaymentsCollapsed}>
            {completedPayments.map(
              (payment: RetailPaymentDetail | GraphQlPayment | Payment) => {
                if (isChewyPayment(payment)) {
                  const clientFullName = Utils.getPersonString(client)

                  return (
                    <ChewyPaymentTile
                      clientFullName={clientFullName}
                      key={payment.paymentMethod.id}
                      orderDate={retailOrder?.orderDate}
                      payment={payment}
                    />
                  )
                }

                return (
                  <InvoicePaymentTile
                    clientId={clientId}
                    invoiceId={invoice.id}
                    isEstimate={isEstimate}
                    key={payment.id}
                    navigateToRefundLanding={navigateToRefundLanding}
                    payment={payment}
                  />
                )
              },
            )}
          </Collapse>
        </Grid>
      )}
      <Grid container item alignItems="center" className={classes.row}>
        <Grid item className={classes.leftColumn} pl={3} xs={8}>
          <Typography.H3>
            {getInvoiceTotalDueLabel(isChewyCheckoutEnabled, isEstimate, t)}
          </Typography.H3>
        </Grid>
        <Grid item pr={3} xs={4}>
          <Typography.H3 sx={{ textAlign: 'right' }}>
            {formatPositiveMoney(invoice.dueToPayNoFee)}
          </Typography.H3>
        </Grid>
      </Grid>
      {overageAmount > 0 && (
        <Grid container item alignItems="center" className={classes.row}>
          <Grid item className={classes.leftColumn} pl={3} xs={8}>
            <Typography.H3>{t('Invoices:PAYMENT_OVERAGE')}</Typography.H3>
          </Grid>
          <Grid item pr={3} xs={4}>
            <Typography.H3 sx={{ textAlign: 'right' }}>
              {formatPositiveMoney(overageAmount)}
            </Typography.H3>
          </Grid>
        </Grid>
      )}
      {flags.showClientBalance && (
        <Grid container item alignItems="center" className={classes.row}>
          <Grid item className={classes.leftColumn} pl={3} xs={8}>
            <Typography.Paragraph>
              {t('Invoices:UPDATED_ACCOUNT_BALANCE_LABEL', {
                date: DateUtils.formatDate(clientBalanceDate),
              })}
            </Typography.Paragraph>
          </Grid>
          <Grid item pr={3} xs={4}>
            <Typography.H3 sx={{ textAlign: 'right' }}>
              {formatMoney(clientBalance)}
            </Typography.H3>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}

export default InvoiceTotalsSection
